import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  Typography,
  Box,
  LinearProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import Papa from "papaparse";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";

export default function UploadRoom() {
  const [previewData, setPreviewData] = useState([]);
  const [csvFile, setCsvFile] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");
  const [examRooms, setExamRooms] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [roomDialogOpen, setRoomDialogOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [savingRoom, setSavingRoom] = useState(false);
  const [roomLookups, setRoomLookups] = useState({ blocks: [], floors: [] });
  const [loadingRoomLookups, setLoadingRoomLookups] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [roomForm, setRoomForm] = useState({
    block_name: "",
    floor_name: "",
    room_slno: "",
    room_no: "",
    room_rows: "",
    room_columns: "",
    room_strength: 0,
    room_type: "",
    room_usage: "REGULAR",
    status: "ACTIVE",
    remarks: "",
  });
  const user = JSON.parse(sessionStorage.getItem("user"));

  const clientId = user?.client_id;
  const collegeId = user?.college_id;
  const user_email = user?.user_email;
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const emptyRoomForm = {
    block_name: "",
    floor_name: "",
    room_slno: "",
    room_no: "",
    room_rows: "",
    room_columns: "",
    room_strength: 0,
    room_type: "",
    room_usage: "REGULAR",
    status: "ACTIVE",
    remarks: "",
  };

  // CSV headers (uploading with block_name, floor_name instead of IDs)
  const csvHeaders = [
    "room_id",
    "college_id",
    "block_name",
    "floor_name",
    "room_slno",
    "room_no",
    "room_rows",
    "room_columns",
    "room_strength",
    "room_type",
    "room_usage",
    "remarks",
    "status",
    "delete_status(0/1)",
  ];
  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };
  const fetchExamRooms = async () => {
    const user = JSON.parse(sessionStorage.getItem("user"));
    setLoadingRooms(true);

    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/exam-rooms-upload/${user.client_id}/${user.college_id}`
      );
      setExamRooms(res.data.data || []);
    } catch (err) {
      console.error(err);
      showSnackbar("Failed to fetch exam rooms", "error");
    }

    setLoadingRooms(false);
  };

  const fetchRoomLookups = async () => {
    if (!clientId || !collegeId) return;
    setLoadingRoomLookups(true);

    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/exam-rooms-upload/${clientId}/${collegeId}/lookups`
      );
      setRoomLookups({
        blocks: res.data?.data?.blocks || [],
        floors: res.data?.data?.floors || [],
      });
    } catch (err) {
      console.error(err);
      showSnackbar("Failed to fetch room lookup data", "error");
      setRoomLookups({ blocks: [], floors: [] });
    } finally {
      setLoadingRoomLookups(false);
    }
  };

  useEffect(() => {
    fetchExamRooms();
    fetchRoomLookups();
  }, []);

  // Download template CSV
  const handleDownloadTemplate = () => {
    const csvContent = csvHeaders.join(",") + "\n";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "exam_rooms_template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // DataGrid columns (add S.No and Errors)
  const columns = [
    { field: "sno", headerName: "S.No", width: 70 },
    { field: "room_id", headerName: "Room ID", width: 100 },
    { field: "college_id", headerName: "College ID", width: 100 },
    { field: "block_name", headerName: "Block Name", width: 160 },
    { field: "floor_name", headerName: "Floor Name", width: 160 },
    { field: "room_slno", headerName: "Room S.No", width: 120 },
    { field: "room_no", headerName: "Room No", width: 120 },
    { field: "room_rows", headerName: "Rows", width: 100 },
    { field: "room_columns", headerName: "Columns", width: 100 },
    { field: "room_strength", headerName: "Strength", width: 120 },
    { field: "room_type", headerName: "Room Type", width: 150 },
    { field: "remarks", headerName: "Remarks", width: 180 },
    { field: "status", headerName: "Status", width: 120 },
    { field: "delete_status", headerName: "Delete Status", width: 130 },
    {
      field: "errors",
      headerName: "Errors",
      width: 250,
      renderCell: (params) => (
        <span style={{ color: "red" }}>
          {params.value && params.value.length > 0 ? params.value.join(", ") : ""}
        </span>
      ),
    },
  ];

  // File selection
  const handleFileChange = (e) => {
    setCsvFile(e.target.files[0]);
    e.target.value = null;
  };

  // Parse and validate
  const handlePreview = () => {
    if (!csvFile) {
      alert("Please choose a CSV file first!");
      return;
    }

    Papa.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      complete: function (results) {
        const validatedData = results.data.map((row, index) => {
          let errors = [];

          // Validation rules
          if (!row.block_name) errors.push("Missing block_name");
          if (!row.floor_name) errors.push("Missing floor_name");
          if (!row.room_no) errors.push("Missing room_no");
          if (row.room_strength && isNaN(row.room_strength)) errors.push("Invalid strength");
          if (row.room_rows && isNaN(row.room_rows)) errors.push("Invalid rows");
          if (row.room_columns && isNaN(row.room_columns)) errors.push("Invalid columns");

          return {
            id: index + 1,
            sno: index + 1,
            ...row,
            errors,
          };
        });
        setPreviewData(validatedData);
      },
    });
  };

  // Confirm upload
  const handleConfirm = async () => {
    try {
      const validRows = previewData.filter((row) => !row.errors || row.errors.length === 0);
      if (validRows.length === 0) {
        showSnackbar("No valid rows to upload!", "warning");
        return;
      }
      setUploading(true);
      setMessage("");
      const rowsWithUserEmail = validRows.map((row) => ({
        ...row,
        created_by: user_email,
        modified_by: user_email,
      }));

      const res = await axios.post(
        `${API_BASE_URL}/api/exam-rooms-upload/${clientId}/${collegeId}/data`,
        { exam_rooms: rowsWithUserEmail }
      );

      showSnackbar(res.data.message || "Room details uploaded successfully!", "success");
      setPreviewData([]);
      setCsvFile(null);
      fetchExamRooms(); // Refresh the list after upload
    } catch (err) {
      if (err.response) {
        setMessage(err.response.data?.message || "Upload failed");
      } else if (err.request) {
        setMessage("No response from server. Please try again later.");
      } else {
        setMessage(`Upload failed: ${err.message}`);
      }
    }
    setUploading(false);
  };

  const handleAddRoomOpen = () => {
    setEditingRoom(null);
    setRoomForm(emptyRoomForm);
    setRoomDialogOpen(true);
  };

  const handleRoomDialogClose = () => {
    if (!savingRoom) {
      setRoomDialogOpen(false);
      setEditingRoom(null);
    }
  };

  const handleRoomFormChange = (field) => (e) => {
    const value = e.target.value;
    setRoomForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "block_name") {
        next.floor_name = "";
      }
      if (field === "room_rows" || field === "room_columns") {
        const rows = Number(field === "room_rows" ? value : next.room_rows) || 0;
        const columns = Number(field === "room_columns" ? value : next.room_columns) || 0;
        next.room_strength = rows * columns;
      }
      return next;
    });
  };

  const floorOptions = useMemo(() => {
    const selectedBlock = roomLookups.blocks.find(
      (block) => block.block_name === roomForm.block_name
    );
    if (!selectedBlock) return roomLookups.floors;
    return roomLookups.floors.filter((floor) => floor.block_id === selectedBlock.block_id);
  }, [roomLookups, roomForm.block_name]);

  const handleRoomSave = async () => {
    const blockName = roomForm.block_name.trim();
    const floorName = roomForm.floor_name.trim();
    const roomNo = roomForm.room_no.trim();

    if (!blockName || !floorName || !roomNo) {
      setMessage("Block name, floor name, and room no are required");
      return;
    }

    try {
      setSavingRoom(true);
      setMessage("");

      const roomPayload = {
        ...roomForm,
        block_name: blockName,
        floor_name: floorName,
        room_no: roomNo,
        room_rows: Number(roomForm.room_rows) || 0,
        room_columns: Number(roomForm.room_columns) || 0,
        room_strength: Number(roomForm.room_strength) || 0,
        room_slno: Number(roomForm.room_slno) || 0,
        created_by: user_email,
        modified_by: user_email,
      };

      if (editingRoom) {
        const response = await axios.put(
          `${API_BASE_URL}/api/exam-rooms-upload/${clientId}/${collegeId}/${editingRoom.room_id}/update-rows-columns`,
          roomPayload
        );
        showSnackbar(response.data.message || "Room updated successfully", "success");
      } else {
        const res = await axios.post(
          `${API_BASE_URL}/api/exam-rooms-upload/${clientId}/${collegeId}/data`,
          { exam_rooms: [roomPayload] }
        );
        showSnackbar("Room added successfully", "success");
      }

      setRoomDialogOpen(false);
      setEditingRoom(null);
      fetchExamRooms();
    } catch (err) {
      showSnackbar(err.response?.data?.message || "Failed to save room", "error");
    } finally {
      setSavingRoom(false);
    }
  };

  const handleDeleteClick = (room) => {
    setSelectedRoom(room);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/api/exam-rooms-upload/${clientId}/${collegeId}/${selectedRoom.room_id}`
      );

      const { success, message } = response.data;

      if (success) {
        setExamRooms((prev) => prev.filter((room) => room.room_id !== selectedRoom.room_id));
        setDeleteDialogOpen(false);
        setSelectedRoom(null);
        showSnackbar(response.data.message || "Room deleted successfully", "error");
      } else {
        showSnackbar(response.data.message || "Failed to delete room", "error");
      }
    } catch (error) {
      console.error("Failed to delete room:", error);
      showSnackbar(
        error.response?.data?.message || "An error occurred while deleting the room",
        "error"
      );
    }
    setDeleteDialogOpen(false);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setSelectedRoom(null);
  };

  const handleToggleStatus = async (room) => {
    try {
      const newStatus = room.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";

      const response = await axios.put(
        `${API_BASE_URL}/api/exam-rooms-upload/${clientId}/${collegeId}/${room.room_id}/toggle-status`,
        { status: newStatus }
      );

      if (response.data.success) {
        setExamRooms((prev) =>
          prev.map((r) => (r.room_id === room.room_id ? { ...r, status: newStatus } : r))
        );
        showSnackbar(`Room status changed to ${newStatus}`, "success");
      } else {
        showSnackbar("Failed to update status", "success");
      }
    } catch (error) {
      console.error("Status update failed:", error);
      showSnackbar(error.response?.data?.message || "Error updating status", "success");
    }
  };

  const handleToggleUsage = async (room) => {
    try {
      const newUsage = room.room_usage === "REGULAR" ? "SUPPLY" : "REGULAR";

      const response = await axios.put(
        `${API_BASE_URL}/api/exam-rooms-upload/${clientId}/${collegeId}/${room.room_id}/toggle-room_usage`,
        { room_usage: newUsage }
      );

      if (response.data.success) {
        setExamRooms((prev) =>
          prev.map((r) => (r.room_id === room.room_id ? { ...r, room_usage: newUsage } : r))
        );
        showSnackbar(`Room Usage changed to ${newUsage}`, "success");
      } else {
        setMessage("Failed to update usage");
      }
    } catch (error) {
      console.error("Usage update failed:", error);
      setMessage(error.response?.data?.message || "Error updating room usage");
    }
  };

  const handleEditClick = (room) => {
    setEditingRoom(room);
    setRoomForm({
      block_name: room.block_name || "",
      floor_name: room.floor_name || "",
      room_slno: room.room_slno || "",
      room_no: room.room_no || "",
      room_rows: room.room_rows || "",
      room_columns: room.room_columns || "",
      room_strength: Number(room.room_rows || 0) * Number(room.room_columns || 0),
      room_type: room.room_type || "",
      room_usage: room.room_usage || "REGULAR",
      status: room.status || "ACTIVE",
      remarks: room.remarks || "",
    });
    setRoomDialogOpen(true);
  };

  const roomColumns = [
    { field: "sno", headerName: "S.No", width: 70 },
    { field: "block_name", headerName: "Block Name", width: 150 },
    { field: "floor_name", headerName: "Floor Name", width: 150 },
    { field: "room_no", headerName: "Room No", width: 120 },
    { field: "room_rows", headerName: "Rows", width: 80 },
    { field: "room_columns", headerName: "Columns", width: 80 },
    { field: "room_strength", headerName: "Strength", width: 80 },
    {
      field: "room_usage",
      headerName: "Room Usage",
      width: 120,
      renderCell: (params) => (
        <Button
          variant="contained"
          size="medium"
          color={params.row.room_usage === "REGULAR" ? "success" : "warning"}
          onClick={() => handleToggleUsage(params.row)}
          sx={{
            color: params.row.room_usage === "REGULAR" ? "#c31111ff" : "#000",
          }}
        >
          {params.row.room_usage}
        </Button>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => (
        <Button
          variant="contained"
          size="medium"
          color={params.row.status === "ACTIVE" ? "success" : "warning"}
          onClick={() => handleToggleStatus(params.row)}
          sx={{
            color: params.row.room_usage === "REGULAR" ? "#131aa8ff" : "#000",
          }}
        >
          {params.row.status}
        </Button>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 180,
      minWidth: 180,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
            width: "100%",
          }}
        >
          <Button
            variant="contained"
            size="small"
            onClick={() => handleEditClick(params.row)}
            sx={{
              backgroundColor: "#1976d2",
              color: "#fff",
              fontWeight: 600,
              textTransform: "none",
              minWidth: 65,
              "&:hover": {
                backgroundColor: "#1565c0",
              },
            }}
          >
            Edit
          </Button>

          <Button
            variant="contained"
            size="small"
            onClick={() => handleDeleteClick(params.row)}
            sx={{
              backgroundColor: "#d32f2f",
              color: "#fff",
              fontWeight: 600,
              textTransform: "none",
              minWidth: 70,
              "&:hover": {
                backgroundColor: "#b71c1c",
              },
            }}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          mb: 2,
        }}
      >
        <Typography
          variant="h5"
          fontWeight={600}
          sx={{
            fontSize: {
              xs: "1rem",
              sm: "1.2rem",
              md: "1.4rem",
              lg: "1.5rem",
            },
          }}
        >
          UPLOAD ROOMS OR ADD ROOMS
        </Typography>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddRoomOpen}
          sx={{
            backgroundColor: "#1976d2",
            color: "#fff",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#115293",
            },
          }}
        >
          Add Room
        </Button>
      </Box>

      {/* Download Template */}
      <Button
        variant="contained"
        startIcon={<DownloadIcon />}
        onClick={handleDownloadTemplate}
        sx={{
          ml: 2,
          mt: 1,
          backgroundColor: "#388e3c",
          color: "#fff",
          "&:hover": { backgroundColor: "#2c6b2f" },
        }}
      >
        Download CSV
      </Button>

      {/* File Upload */}
      <input
        type="file"
        accept=".csv"
        style={{ display: "none" }}
        id="upload-csv"
        onChange={handleFileChange}
      />
      <label htmlFor="upload-csv">
        <Button
          variant="contained"
          component="span"
          startIcon={<CloudUploadIcon />}
          sx={{
            ml: 2,
            mt: 1,
            backgroundColor: "#1976d2",
            color: "#fff",
            "&:hover": { backgroundColor: "#115293" },
          }}
        >
          Choose CSV
        </Button>
      </label>

      <Button
        sx={{
          ml: 2,
          mt: 1,
          backgroundColor: "#ff9800",
          color: "#fff",
          "&:hover": { backgroundColor: "#c77700" },
        }}
        variant="contained"
        onClick={handlePreview}
      >
        Preview
      </Button>

      <Button
        sx={{
          ml: 2,
          mt: 1,
          backgroundColor: "#4caf50",
          color: "#fff",
          "&:hover": { backgroundColor: "#357a38" },
        }}
        variant="contained"
        onClick={handleConfirm}
      >
        Confirm & Upload
      </Button>

      {csvFile && (
        <Typography variant="body1" sx={{ mb: 2, mt: 2 }}>
          Selected: {csvFile.name}
        </Typography>
      )}

      {uploading && <LinearProgress sx={{ mt: 2 }} />}

      {previewData.length > 0 && (
        <Box sx={{ mt: 3, height: 500 }}>
          <DataGrid
            sx={{ mt: 2, ml: 2, mr: 2, height: 650 }}
            rows={previewData}
            columns={columns}
            getRowId={(row) => row.sno}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            disableRowSelectionOnClick
            pagination
            disableSelectionOnClick
            disableColumnMenu
            autoHeight
          />
        </Box>
      )}

      {examRooms.length > 0 && (
        <Box
          sx={{
            mt: 3,
            width: "100%",
            height: 700,
            overflowX: "auto",
          }}
        >
          <DataGrid
            sx={{
              mt: 2,
              height: 650,
              minWidth: 1300,
              width: "100%",
            }}
            rows={examRooms.map((room, index) => ({
              id: index + 1,
              sno: index + 1,
              room_id: room.room_id,
              college_id: room.college_id,
              block_name: room.block?.block_name || "",
              floor_name: room.floor?.floor_name || "",
              room_no: room.room_no,
              room_slno: room.room_slno,
              room_rows: room.room_rows,
              room_columns: room.room_columns,
              room_strength: room.room_strength,
              room_type: room.room_type,
              remarks: room.remarks,
              room_usage: room.room_usage,
              status: room.status,
              delete_status: room.delete_status,
            }))}
            columns={roomColumns}
            loading={loadingRooms}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            disableRowSelectionOnClick
            pagination
            disableSelectionOnClick
          />
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCancelDelete}>
        <DialogTitle
          sx={{
            backgroundColor: "#d32f2f",
            color: "#fff",
            fontWeight: 600,
          }}
        >
          Delete Exam Room
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          <DialogContentText
            sx={{
              backgroundColor: "#fff",
              color: "#0e0202",
              fontWeight: 600,
            }}
          >
            Are you sure you want to delete room <strong>{selectedRoom?.room_no}</strong>? This
            action cannot be undone.
          </DialogContentText>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCancelDelete}
            variant="contained"
            sx={{
              backgroundColor: "#866219",
              color: "#fff",
              "&:hover": {
                backgroundColor: "#68a21b",
              },
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{
              backgroundColor: "#d32f2f",
              color: "#fff",
              "&:hover": {
                backgroundColor: "#b71c1c",
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Room Dialog */}
      <Dialog open={roomDialogOpen} onClose={handleRoomDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editingRoom ? "Edit Room" : "Add Room"}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 1 }}>
            {/* First row: Block Name, Floor Name, Room No */}
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                select
                label="Block Name"
                value={roomForm.block_name}
                onChange={handleRoomFormChange("block_name")}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true, displayEmpty: true }}
                fullWidth
                required
                disabled={loadingRoomLookups}
              >
                <option value="" disabled hidden>
                  Select Block Name
                </option>
                {roomLookups.blocks.map((block) => (
                  <option key={block.block_id} value={block.block_name}>
                    {block.block_name}
                  </option>
                ))}
              </TextField>
              <TextField
                select
                label="Floor Name"
                value={roomForm.floor_name}
                onChange={handleRoomFormChange("floor_name")}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true, displayEmpty: true }}
                fullWidth
                required
                disabled={loadingRoomLookups}
              >
                <option value="" disabled hidden>
                  Select Floor Name
                </option>
                {floorOptions.map((floor) => (
                  <option key={floor.floor_id} value={floor.floor_name}>
                    {floor.floor_name}
                  </option>
                ))}
              </TextField>
              <TextField
                label="Room No"
                value={roomForm.room_no}
                onChange={handleRoomFormChange("room_no")}
                fullWidth
                required
              />
            </Box>
            {/* Second row: Rows, Columns, Strength */}
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Room S.No"
                type="number"
                value={roomForm.room_slno}
                onChange={handleRoomFormChange("room_slno")}
                fullWidth
              />
              <TextField
                label="Room Type"
                value={roomForm.room_type}
                onChange={handleRoomFormChange("room_type")}
                fullWidth
              />
              <TextField
                label="Remarks"
                value={roomForm.remarks}
                onChange={handleRoomFormChange("remarks")}
                fullWidth
              />
            </Box>
            {/* Third row: Rows, Columns, Strength */}
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Rows"
                type="number"
                value={roomForm.room_rows}
                onChange={handleRoomFormChange("room_rows")}
                fullWidth
              />
              <TextField
                label="Columns"
                type="number"
                value={roomForm.room_columns}
                onChange={handleRoomFormChange("room_columns")}
                fullWidth
              />
              <TextField
                label="Strength"
                value={roomForm.room_strength}
                InputProps={{ readOnly: true }}
                fullWidth
              />
            </Box>
            {/* Fourth row: Room Usage, Status */}
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                select
                label="Room Usage"
                value={roomForm.room_usage}
                onChange={handleRoomFormChange("room_usage")}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true, displayEmpty: true }}
                fullWidth
              >
                <option value="" disabled hidden>
                  Select Room Usage
                </option>
                <option value="REGULAR">REGULAR</option>
                <option value="SUPPLY">SUPPLY</option>
              </TextField>
              <TextField
                select
                label="Status"
                value={roomForm.status}
                onChange={handleRoomFormChange("status")}
                InputLabelProps={{ shrink: true }}
                SelectProps={{ native: true, displayEmpty: true }}
                fullWidth
              >
                <option value="" disabled hidden>
                  Select Status
                </option>
                <option value="ACTIVE">ACTIVE</option>
                <option value="INACTIVE">INACTIVE</option>
              </TextField>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRoomDialogClose} disabled={savingRoom}>
            Cancel
          </Button>
          <Button
            onClick={handleRoomSave}
            color="primary"
            variant="contained"
            disabled={savingRoom}
            sx={{ color: "#fff" }}
          >
            {savingRoom ? "Saving..." : editingRoom ? "Update Room" : "Add Room"}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          variant="filled"
          sx={{
            width: "100%",
            fontWeight: 600,
            minWidth: 300,
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
